import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import PropTypes from 'prop-types';

export default function AddTicketsDialog({ open, onClose, user, onConfirm }) {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setAmount('');
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    const ticketsAmount = parseInt(amount, 10);
    
    if (!amount || isNaN(ticketsAmount) || ticketsAmount <= 0) {
      setError('Please enter a valid positive number');
      return;
    }

    if (!user) {
      setError('No user selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onConfirm(user.user_id, ticketsAmount);
      handleClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add tickets';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow only numbers
    if (value === '' || /^\d+$/.test(value)) {
      setAmount(value);
      setError(null);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Tickets</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {user && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                User: <strong>{user.username}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current Tickets: <strong>{user.tickets}</strong>
              </Typography>
            </Box>
          )}

          <TextField
            autoFocus
            fullWidth
            label="Amount of Tickets"
            type="text"
            value={amount}
            onChange={handleAmountChange}
            placeholder="Enter number of tickets to add"
            disabled={loading}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          disabled={loading || !amount}
        >
          {loading ? 'Adding...' : 'Add Tickets'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddTicketsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.shape({
    user_id: PropTypes.number,
    username: PropTypes.string,
    tickets: PropTypes.number,
  }),
  onConfirm: PropTypes.func.isRequired,
};
